import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

interface VideoFrameThumbProps {
  /** Video URL to extract a frame from. Null / undefined renders the placeholder. */
  videoUrl?: string | null;
  /** className applied to the resulting <img> when extraction succeeds. */
  className?: string;
  alt?: string;
  /** Rendered while the frame is loading AND if extraction fails (CORS, codec, etc.). */
  placeholder: ReactNode;
  /** Optional seek target in seconds. Defaults to min(1s, 10% of duration). */
  seekTo?: number;
}

/**
 * Lightweight client-side video frame extractor. Mounts a hidden <video>,
 * seeks to ~1s, draws to a canvas, and emits a JPEG data URL. Falls back
 * to the supplied placeholder while loading or on failure — and failure
 * is realistic: cross-origin reads can taint the canvas, mobile Safari
 * has quirks around `currentTime` before play, and Firebase Storage CORS
 * config may or may not allow anonymous canvas access from the app origin.
 *
 * This is the lightest viable thumbnail path until a server-side
 * extraction pipeline (Cloud Function + ffmpeg, or a thumbnailing
 * extension) exists. When that lands, this component becomes redundant
 * for any field-backed thumbnail; keep it for fallback.
 */
export default function VideoFrameThumb({
  videoUrl,
  className,
  alt,
  placeholder,
  seekTo,
}: VideoFrameThumbProps) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setDataUrl(null);
    setFailed(false);
    if (!videoUrl) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    let extracted = false;

    const video = document.createElement('video');
    // Required for canvas access to a cross-origin video; if the response
    // doesn't include the right CORS headers the load itself will fail
    // and we'll fall through to the placeholder via the error handler.
    video.crossOrigin = 'anonymous';
    video.muted = true;
    // playsInline keeps iOS from launching the native player as a side
    // effect of touching `currentTime`.
    video.playsInline = true;
    video.preload = 'metadata';
    video.src = videoUrl;

    const handleLoadedMetadata = () => {
      const target = seekTo ?? Math.min(1, (video.duration || 1) * 0.1);
      video.currentTime = Math.max(0, target);
    };

    const handleSeeked = () => {
      if (extracted || cancelled) return;
      extracted = true;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 180;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          if (!cancelled) setFailed(true);
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const data = canvas.toDataURL('image/jpeg', 0.7);
        if (!cancelled) setDataUrl(data);
      } catch {
        // SecurityError when the canvas is tainted by a cross-origin
        // video without CORS — fall back to placeholder gracefully.
        if (!cancelled) setFailed(true);
      } finally {
        // Best-effort release of the underlying media resource.
        video.removeAttribute('src');
        video.load();
      }
    };

    const handleError = () => {
      if (!cancelled) setFailed(true);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('error', handleError);

    // Safety net: if metadata never loads (slow network, server timeout,
    // permanently blocked), fall through to the placeholder after 5s.
    const timeoutId = setTimeout(() => {
      if (!extracted && !cancelled) setFailed(true);
    }, 5000);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('error', handleError);
      video.removeAttribute('src');
      try {
        video.load();
      } catch {
        // Ignore — video element is being torn down anyway.
      }
    };
  }, [videoUrl, seekTo]);

  if (failed || !dataUrl) {
    return <>{placeholder}</>;
  }

  return <img src={dataUrl} alt={alt ?? ''} className={className} />;
}
